import { DATE_FORMAT } from '@/helpers/dates';
import { useDeleteProjectTransactionMutation, useUpdateProjectTransactionStatusMutation } from '@/services/payouts';
import { Box, Button, ButtonTone, Popover, toast } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { SyntheticEvent, useCallback, useMemo, useState } from 'react';

import { IsoDateString, PayoutTransactionLevel, ProjectTransactionStatus } from 'types';

import { PausedTransactionModal } from './admin/PausedTransactionModal';

/**
 * The fields these two actions need, at either transaction level. Kept narrow rather than taking a
 * client transaction type so the same menu serves the project and payee tables, whose row types
 * differ.
 */
export interface ActionableTransaction {
  transactionId: string;
  status: ProjectTransactionStatus;
  transactionLevel?: PayoutTransactionLevel;
  pauseExpirationDate?: IsoDateString;
}

/** Mirrors the backend's allow-list in deleteTransaction; approved and paid are absent from both. */
const deletableStatuses = new Set<ProjectTransactionStatus>([
  ProjectTransactionStatus.open,
  ProjectTransactionStatus.pending,
  ProjectTransactionStatus.historical,
  ProjectTransactionStatus.adjustment,
  ProjectTransactionStatus.paused,
  ProjectTransactionStatus.migrated,
]);

const pausableStatuses = new Set<ProjectTransactionStatus>([
  ProjectTransactionStatus.open,
  ProjectTransactionStatus.pending,
  ProjectTransactionStatus.paused,
]);

/**
 * Only a project has a milestone schedule that a deletion rebalances, so the recalculation warning
 * would describe something that does not happen on an organization transaction.
 */
const deleteConfirmation = (level?: PayoutTransactionLevel) =>
  level === PayoutTransactionLevel.payee
    ? 'Deleting this transaction cannot be undone.'
    : 'Deleting this transaction will cause all open or pending transactions to be recalculated.  This action cannot be undone.';

interface TransactionActionsMenuProps {
  transaction: ActionableTransaction;
}

export const TransactionActionsMenu = ({ transaction }: TransactionActionsMenuProps) => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isPauseModalOpen, setPauseModalOpen] = useState(false);
  const [deleteTransaction] = useDeleteProjectTransactionMutation();
  const [updateTransactionStatus] = useUpdateProjectTransactionStatusMutation();

  const isPaused = transaction.status === ProjectTransactionStatus.paused;

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const toggleMenu = useCallback(() => setMenuOpen((open) => !open), []);
  const closePauseModal = useCallback(() => setPauseModalOpen(false), []);

  const handleDelete = useCallback(async () => {
    if (!window.confirm(deleteConfirmation(transaction.transactionLevel))) return;

    try {
      await deleteTransaction({ transactionId: transaction.transactionId }).unwrap();
      toast.success('Transaction deleted successfully');
    } catch (_e: any) {
      toast.error('Error deleting transaction');
    }
  }, [deleteTransaction, transaction.transactionId, transaction.transactionLevel]);

  const handlePause = useCallback(() => {
    setPauseModalOpen(true);
    setMenuOpen(false);
  }, []);

  const handleSubmitPause = useCallback(
    async (values: any) => {
      // The form holds MM-dd-yyyy while the stored date is ISO, so the two have to be compared in
      // the same shape — otherwise this never matches and re-saving an unchanged date writes a
      // pointless history entry.
      const expirationDate = DateTime.fromFormat(values.expirationDate, DATE_FORMAT).toISODate();
      if (expirationDate === transaction.pauseExpirationDate) {
        toast.error('Pause expiration date is the same as current one');
        return;
      }

      // Deliberately not caught: the modal closes and resets only when this resolves, so swallowing
      // a rejection here would report a failed pause as done and throw the user's input away. Its
      // own handler shows the error and leaves the form open.
      await updateTransactionStatus({
        transactionId: transaction.transactionId,
        status: ProjectTransactionStatus.paused,
        pauseExpirationDate: expirationDate || undefined,
        pauseReason: values.reason,
      }).unwrap();
      toast.success('Transaction paused successfully');
    },
    [transaction.pauseExpirationDate, transaction.transactionId, updateTransactionStatus],
  );

  const menuItems = useMemo(
    () => [
      ...(deletableStatuses.has(transaction.status)
        ? [{ label: 'Delete Transaction', onClick: handleDelete, tone: 'danger' as ButtonTone }]
        : []),
      ...(pausableStatuses.has(transaction.status)
        ? [
            {
              label: isPaused ? 'Update Expiration' : 'Pause Transaction',
              onClick: handlePause,
              tone: 'neutral' as ButtonTone,
            },
          ]
        : []),
    ],
    [transaction.status, isPaused, handleDelete, handlePause],
  );

  if (!menuItems.length) return null;

  return (
    <Box>
      <Popover
        trigger="click"
        placement="bottom-start"
        onClickOutside={closeMenu}
        contentContainerProps={{
          padding: 'sm',
          shadow: 'lg',
          radius: 'sm',
          overflow: 'hidden',
          borderColor: 'separator',
          borderWidth: 'xs',
          color: 'body-primary',
          fontSize: 'sm',
          maxWidth: '3xl',
        }}
        hasArrow
        isOpen={isMenuOpen}
        content={
          <Box gap="sm" direction="column">
            {menuItems.map((item) => (
              <MenuItemButton key={item.label} label={item.label} tone={item.tone} onSelect={item.onClick} />
            ))}
          </Box>
        }
      >
        <PausedTransactionModal
          isOpen={isPauseModalOpen}
          modalId="pauseTransactionModal"
          modalTitle={isPaused ? 'Update Expiration' : 'Pause Transaction'}
          submitButtonText="Confirm Pause"
          handleClose={closePauseModal}
          transaction={transaction}
          handleSubmit={handleSubmitPause}
        />
        <Button
          iconPrefix="dots"
          variant="tertiary"
          size="md"
          tone="neutral"
          aria-label="transactionActions"
          onClick={toggleMenu}
        />
      </Popover>
    </Box>
  );
};

/**
 * Extracted so the click handler is stable per item rather than rebuilt inside the map, which the
 * no-inline-arrow rule for JSX props forbids.
 */
const MenuItemButton = ({
  label,
  tone,
  onSelect,
}: {
  label: string;
  tone: ButtonTone;
  onSelect: () => void | Promise<void>;
}) => {
  const handleClick = useCallback(
    (_event: SyntheticEvent) => {
      void onSelect();
    },
    [onSelect],
  );

  return (
    <Button tone={tone} onClick={handleClick}>
      {label}
    </Button>
  );
};
